/**
 * Aba "Queimadas" da barra lateral: fontes, modo de exibição, filtros,
 * aparência, situação dos dados, legenda e explicação do que é um foco.
 */
import { Activity, CircleQuestionMark, ExternalLink, Flame, MapPinned, Palette, RefreshCw, Satellite } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTimeStore } from '../state/time';
import {
  Badge,
  Button,
  ColorInput,
  Hint,
  ProgressBar,
  Section,
  Segmented,
  Slider,
  Toggle,
  type SelectOption,
} from '../ui/primitives';
import { CLASS_GOES, CLASS_OTHERS, computeCounts, GOES_SATELLITE, KNOWN_SATELLITES, sortSatellites } from './dataset';
import { formatInt } from '../lib/format';
import { formatShortDateBrt, formatTimeBrt, formatTimeUtc } from '../lib/time';
import { formatDecimal } from './format';
import { FLAME_EXTENT, FLAME_INNER_WHITE, FLAME_SVG } from './flame';
import { levelLook } from './look';
import { useAreaFilterStore } from './areaFilter';
import { areaKind } from './areaSearch';
import { REFERENCE_SATELLITE, STYLE_LIMITS, useFireStore } from './store';
import { refreshFires } from './sync';
import { LEVEL_NORMAL, LEVEL_TRAIL_1, LEVEL_TRAIL_2 } from './timeFilter';
import type { DisplayMode, FireSource, SourceStatus } from './types';

const MODE_OPTIONS: SelectOption<DisplayMode>[] = [
  { value: 'frame', label: 'Quadro atual' },
  { value: 'accum', label: 'Acumulado' },
  { value: 'all', label: 'Todo o período' },
];

const MODE_HINTS: Record<DisplayMode, string> = {
  frame: 'Só os focos detectados no intervalo do quadro exibido.',
  accum:
    'Soma os focos desde o início do período. Os do quadro atual brilham, e o brilho diminui aos poucos nos 2 quadros seguintes; os mais antigos ficam menores e apagados.',
  all: 'Todos os focos do período de uma vez, sem animação.',
};

export function FirePanel() {
  const sources = useFireStore((s) => s.sources);
  const mode = useFireStore((s) => s.mode);
  const onlyAmazonia = useFireStore((s) => s.onlyAmazonia);
  const amz = useFireStore((s) => s.amazoniaFilter);
  const setSource = useFireStore((s) => s.setSource);
  const setMode = useFireStore((s) => s.setMode);
  const setOnlyAmazonia = useFireStore((s) => s.setOnlyAmazonia);
  const status = useFireStore((s) => s.status);
  const saveData = useFireStore((s) => s.saveData);

  const amzHint =
    amz.diario === 'municipios'
      ? 'Pelos municípios da Amazônia Legal (IBGE).'
      : 'Filtro aproximado (por estado) enquanto a lista de municípios não carrega.';

  return (
    <div>
      <Section title="Focos de queimada" icon={Flame} right={<FrameBadge />}>
        <Toggle
          checked={sources.goes10min}
          onChange={(v) => setSource('goes10min', v)}
          label="GOES-19 — a cada 10 minutos"
          description="Satélite que observa a região o tempo todo. Os focos chegam com cerca de 20 min de atraso."
          right={<StatusDot status={status.goes10min} />}
        />
        <Toggle
          checked={sources.diario}
          onChange={(v) => setSource('diario', v)}
          label="Todos os satélites — diário"
          description="Inclui satélites que passam poucas vezes por dia, com município, bioma e risco de fogo. Atualizado de hora em hora."
          right={<StatusDot status={status.diario} />}
        />
        {saveData && !sources.diario && (
          <Hint>
            Economia de dados ligada neste aparelho: o arquivo diário (cerca de 3,6 MB por dia) começa desligado. Ligue se
            quiser ver todos os satélites.
          </Hint>
        )}
        <Toggle checked={onlyAmazonia} onChange={setOnlyAmazonia} label="Somente Amazônia Legal" description={amzHint} />
        <AreaFilterNotice />
        <Segmented label="Mostrar" value={mode} options={MODE_OPTIONS} onChange={setMode} size="sm" />
        <Hint>{MODE_HINTS[mode]}</Hint>
        <Counts />
      </Section>

      <Section title="Situação dos dados" icon={Activity} right={<RefreshButton />}>
        <SourceStatusView source="goes10min" title="GOES-19 (10 min)" />
        <SourceStatusView source="diario" title="Todos os satélites (diário)" />
      </Section>

      <SatelliteSection />
      <StyleSection />
      <LegendSection />

      <Section title="O que é um foco?" icon={CircleQuestionMark} defaultOpen={false}>
        <p className="text-xs leading-relaxed text-ink-200">
          Um foco é um <strong>pixel de imagem de satélite com temperatura muito alta</strong>, sinal de fogo ativo no
          momento da passagem.
        </p>
        <ul className="list-disc space-y-1 pl-4 text-xs leading-relaxed text-ink-300">
          <li>Não é o tamanho da área queimada.</li>
          <li>Um incêndio grande pode gerar vários focos, e o mesmo fogo aparece de novo a cada passagem.</li>
          <li>Nuvens escondem focos, e pode haver falsos positivos (superfícies muito quentes, por exemplo).</li>
        </ul>
        <a
          href="https://terrabrasilis.dpi.inpe.br/queimadas/portal/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-mata-300 hover:text-mata-400"
        >
          Fonte: INPE — Programa Queimadas <ExternalLink className="size-3" aria-hidden />
        </a>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** "14 set 21:20" (Brasília; o rótulo do fuso fica com quem chama). */
const brt = (t: number) => `${formatShortDateBrt(t)} ${formatTimeBrt(t)}`;

/** Contagens do quadro/período — um só snapshot do loop e dos dados (reage ao quadro atual). */
/**
 * Aviso do filtro por áreas (definido nos "Últimos focos"): o mapa e os
 * números desta aba mostram só os focos dessas áreas.
 */
function AreaFilterNotice() {
  const areas = useAreaFilterStore(useShallow((s) => s.areas));
  const clearAreas = useAreaFilterStore((s) => s.clearAreas);
  const state = useFireStore((s) => s.areaStatus.state);
  if (!areas.length) return null;
  const names = areas.map((a) => a.name ?? `${areaKind(a.layer).label} ${a.key}`).join(', ');
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-fogo-500/40 bg-fogo-500/10 px-2.5 py-2 text-xs text-ink-100"
      role="status"
      data-testid="area-filter-notice"
    >
      <MapPinned className="mt-0.5 size-3.5 shrink-0 text-fogo-400" aria-hidden />
      <p className="min-w-0 flex-1 leading-snug">
        {state === 'error' ? (
          <>Não foi possível aplicar o filtro por áreas ({names}): mostrando todos os focos.</>
        ) : (
          <>
            Só focos em: <strong className="font-semibold text-white">{names}</strong>
            {state === 'loading' && <span className="text-ink-300"> (carregando limites…)</span>}
          </>
        )}
      </p>
      <Button size="sm" variant="ghost" onClick={clearAreas} className="-my-1 shrink-0">
        Limpar
      </Button>
    </div>
  );
}

function useWindowCounts() {
  const dataset = useFireStore((s) => s.dataset);
  const mode = useFireStore((s) => s.mode);
  const loop = useTimeStore(
    useShallow((s) => ({ frames: s.frames, index: s.index, currentTime: s.currentTime, range: s.range })),
  );
  return computeCounts(dataset, mode, loop);
}

function FrameBadge() {
  const c = useWindowCounts();
  if (!c) return null;
  return <Badge tone={c.frame.total ? 'fogo' : 'neutral'}>{formatInt(c.frame.total)} no quadro</Badge>;
}

function Counts() {
  const c = useWindowCounts();
  const mode = useFireStore((s) => s.mode);
  const tenOn = useFireStore((s) => s.sources.goes10min);
  const publicado = useFireStore((s) => s.status.goes10min.publicadoAte ?? s.status.goes10min.latest);
  if (!c) return <Hint>Os focos aparecem quando o loop de imagens carregar.</Hint>;
  // As imagens chegam antes dos focos: nos últimos quadros o INPE ainda não
  // publicou. Hora publicada sem nenhum foco não conta como pendente.
  const pending = tenOn && publicado !== null && c.now > publicado;
  return (
    <>
      {pending && (
        <Hint>
          Os focos do GOES-19 deste horário ainda não foram publicados pelo INPE (chegam cerca de 20 min depois das
          imagens). Publicados até {brt(publicado)} (Brasília).
        </Hint>
      )}
      <div className="grid grid-cols-2 gap-2 text-center">
        <Stat
          label="No quadro atual"
          value={pending ? null : c.frame.total}
          sub={pending ? 'ainda não publicados' : `${brt(c.now)} (Brasília)`}
        />
        <Stat
          label={mode === 'accum' ? 'Acumulados até agora' : 'No período'}
          value={mode === 'accum' ? c.shown.total : c.period.total}
          sub={mode === 'accum' ? `de ${formatInt(c.period.total)} no período` : 'na janela do loop'}
        />
      </div>
    </>
  );
}

function Stat(props: { label: string; value: number | null; sub: string }) {
  return (
    <div className="rounded-lg bg-ink-800/80 px-2 py-2">
      <div className="text-[11px] text-ink-400">{props.label}</div>
      <div className="font-mono text-lg font-semibold text-white tabular-nums">{props.value === null ? '—' : formatInt(props.value)}</div>
      <div className="text-[10px] text-ink-400">{props.sub}</div>
    </div>
  );
}

function StatusDot(props: { status: SourceStatus }) {
  const { state } = props.status;
  const cls =
    state === 'loading'
      ? 'bg-yellow-300 animate-pulse'
      : state === 'error'
        ? 'bg-brasa-600'
        : state === 'ready'
          ? 'bg-mata-400'
          : 'bg-ink-600';
  const label = { off: 'Desligado', idle: 'Aguardando', loading: 'Carregando', ready: 'Atualizado', error: 'Erro' }[state];
  return <span className={`mt-1.5 inline-block size-2 shrink-0 rounded-full ${cls}`} title={label} aria-label={label} />;
}

function RefreshButton() {
  return (
    <Button size="sm" variant="ghost" icon={RefreshCw} onClick={refreshFires} title="Buscar focos novos no INPE">
      Atualizar
    </Button>
  );
}

function SourceStatusView(props: { source: FireSource; title: string }) {
  const st = useFireStore((s) => s.status[props.source]);
  const on = useFireStore((s) => s.sources[props.source]);
  const range = useTimeStore((s) => s.range);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs">
        <StatusDot status={st} />
        <span className="font-medium text-ink-100">{props.title}</span>
        {st.state === 'loading' && <span className="ml-auto text-ink-400">{Math.round(st.progress * 100)}%</span>}
      </div>
      {!on ? (
        <Hint>Desligado.</Hint>
      ) : !range ? (
        <Hint>Aguardando o loop de imagens.</Hint>
      ) : st.state === 'loading' ? (
        <>
          <ProgressBar value={st.progress} tone="fogo" />
          <Hint>{st.message ?? 'Carregando…'}</Hint>
        </>
      ) : st.state === 'error' ? (
        <div className="space-y-1.5">
          <p className="text-xs text-red-300">{st.error}</p>
          {st.errorDetail && <p className="text-[10px] text-ink-500">Detalhe: {st.errorDetail}</p>}
          <Button size="sm" icon={RefreshCw} onClick={refreshFires}>
            Tentar de novo
          </Button>
        </div>
      ) : st.state === 'ready' ? (
        <Hint>
          {st.latest ? (
            <>
              Foco mais recente: {brt(st.latest)} (Brasília) · {formatTimeUtc(st.latest)} UTC
              <br />
            </>
          ) : (
            <>
              Nenhum foco nesta janela.
              <br />
            </>
          )}
          {st.message}
          {st.updatedAt ? ` · verificado às ${formatTimeBrt(st.updatedAt)} (Brasília)` : ''}
        </Hint>
      ) : (
        <Hint>Aguardando…</Hint>
      )}
      {on && st.warning && <p className="text-xs leading-snug text-yellow-300">{st.warning}</p>}
      {on && st.note && st.state !== 'error' && <Hint>{st.note}</Hint>}
    </div>
  );
}

function SatelliteSection() {
  const available = useFireStore((s) => s.availableSatellites);
  const hidden = useFireStore((s) => s.hiddenSatellites);
  const toggle = useFireStore((s) => s.toggleSatellite);
  const setHidden = useFireStore((s) => s.setHiddenSatellites);
  const showOnly = useFireStore((s) => s.showOnlySatellite);
  const tenOn = useFireStore((s) => s.sources.goes10min);
  const isOn = (sat: string) => !hidden.includes(sat);
  // Chips: satélites com focos carregados + os ocultos (para poder reexibir).
  const names = sortSatellites([...new Set([...available, ...hidden.filter((h) => available.includes(h) || isKnown(h))])]);
  const refOnly = isOn(REFERENCE_SATELLITE) && names.every((s) => s === REFERENCE_SATELLITE || !isOn(s));
  const hiddenCount = names.filter((s) => !isOn(s)).length;

  return (
    <Section
      title="Satélites"
      icon={Satellite}
      defaultOpen={false}
      right={hiddenCount ? <Badge tone="alerta">{hiddenCount} oculto(s)</Badge> : undefined}
    >
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant={hiddenCount === 0 ? 'primary' : 'secondary'} onClick={() => setHidden([])}>
          Todos
        </Button>
        <Button
          size="sm"
          variant={refOnly ? 'primary' : 'secondary'}
          onClick={() => showOnly(REFERENCE_SATELLITE)}
          title="Só o satélite de referência do INPE"
        >
          Satélite de referência (AQUA_M-T)
        </Button>
      </div>
      {names.length ? (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Mostrar ou esconder satélites">
          {names.map((sat) => (
            <Button
              key={sat}
              size="sm"
              variant={isOn(sat) ? 'primary' : 'secondary'}
              onClick={() => toggle(sat)}
              className="font-mono"
              title={isOn(sat) ? `Esconder ${sat}` : `Mostrar ${sat}`}
            >
              {sat}
            </Button>
          ))}
        </div>
      ) : (
        <Hint>A lista aparece quando os dados carregarem.</Hint>
      )}
      <Hint>
        O INPE usa o AQUA_M-T como referência para comparar dias e anos: ele passa sempre no mesmo horário, no início da
        tarde. Os demais satélites polares passam poucas vezes por dia.
      </Hint>
      {tenOn && !isOn(GOES_SATELLITE) && (
        <p className="text-xs text-yellow-300">O filtro está escondendo o GOES-19 (10 min).</p>
      )}
    </Section>
  );
}

const isKnown = (sat: string) => (KNOWN_SATELLITES as readonly string[]).includes(sat);

function StyleSection() {
  const style = useFireStore((s) => s.style);
  const setStyle = useFireStore((s) => s.setStyle);
  const resetStyle = useFireStore((s) => s.resetStyle);
  return (
    <Section title="Aparência" icon={Palette} defaultOpen={false}>
      <Slider
        label="Tamanho"
        value={style.size}
        min={STYLE_LIMITS.size.min}
        max={STYLE_LIMITS.size.max}
        step={STYLE_LIMITS.size.step}
        onChange={(size) => setStyle({ size })}
        format={(v) => `${formatDecimal(v, 1)} px`}
      />
      <Slider
        label="Opacidade"
        value={Math.round(style.opacity * 100)}
        min={STYLE_LIMITS.opacity.min * 100}
        max={STYLE_LIMITS.opacity.max * 100}
        step={STYLE_LIMITS.opacity.step * 100}
        onChange={(v) => setStyle({ opacity: v / 100 })}
        format={(v) => `${v}%`}
      />
      <ColorInput label="GOES-19" value={style.colorGoes} onChange={(colorGoes) => setStyle({ colorGoes })} />
      <ColorInput label="Outros satélites" value={style.colorOthers} onChange={(colorOthers) => setStyle({ colorOthers })} />
      <Button size="sm" variant="ghost" onClick={resetStyle}>
        Restaurar padrão
      </Button>
    </Section>
  );
}

/** Amostra do símbolo do mapa: a mesma chama (flame.ts), com contorno escuro e brilho. */
function FlameMark(props: { color: string; scale?: number; alpha?: number; glow?: number }) {
  const { color, scale = 1, alpha = 1, glow = 0 } = props;
  const r = Math.max(3, 5 * scale);
  const inner = mixWhite(color, FLAME_INNER_WHITE);
  const style: CSSProperties = {
    width: 2 * r * FLAME_EXTENT,
    height: 2 * r * FLAME_EXTENT,
    opacity: alpha,
    overflow: 'visible',
    filter: glow ? `drop-shadow(0 0 ${glow * 1.5}px ${color})` : undefined,
  };
  const e = FLAME_EXTENT;
  return (
    <span className="inline-flex size-6 shrink-0 items-center justify-center" aria-hidden>
      <svg viewBox={`${-e} ${-e} ${2 * e} ${2 * e}`} style={style}>
        {/* contorno por baixo (só a metade externa aparece, sem as divisas internas) */}
        <path d={FLAME_SVG.outer} fill="none" stroke="rgba(0,0,0,.55)" strokeWidth={2 / r} strokeLinejoin="round" />
        <path d={FLAME_SVG.outer} fill={color} />
        <path d={FLAME_SVG.inner} fill={inner} />
      </svg>
    </span>
  );
}

function mixWhite(hex: string, k: number): string {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  const m = (c: number) => Math.round(c + (255 - c) * k);
  return `rgb(${m((n >> 16) & 255)},${m((n >> 8) & 255)},${m(n & 255)})`;
}

function LegendSection() {
  const style = useFireStore((s) => s.style);
  const old = levelLook(CLASS_OTHERS, LEVEL_NORMAL - 1);
  const t1 = levelLook(CLASS_GOES, LEVEL_TRAIL_1);
  const t2 = levelLook(CLASS_GOES, LEVEL_TRAIL_2);
  return (
    <Section title="Legenda" icon={Flame} defaultOpen={false}>
      <ul className="space-y-1 text-xs text-ink-200">
        <li className="flex items-center gap-2">
          <FlameMark color={style.colorGoes} glow={2} /> GOES-19 (a cada 10 min)
        </li>
        <li className="flex items-center gap-2">
          <FlameMark color={style.colorOthers} /> Outros satélites (diário)
        </li>
        <li className="flex items-center gap-2">
          <FlameMark color={style.colorGoes} scale={1.3} glow={4} /> Detectado no quadro atual (brilho)
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-flex items-center" aria-hidden>
            <FlameMark color={style.colorGoes} scale={t1.scale} glow={3} />
            <FlameMark color={style.colorGoes} scale={t2.scale} glow={2} />
          </span>
          Detectado 1 e 2 quadros antes (o brilho diminui aos poucos)
        </li>
        <li className="flex items-center gap-2">
          <FlameMark color={style.colorOthers} scale={old.scale} alpha={old.alpha} /> Mais antigo (modo acumulado)
        </li>
      </ul>
    </Section>
  );
}
